import React, { useEffect, useRef, useState } from 'react';
import { User } from '../types';
import { auth, isFirebaseReady } from '../services/firebase';
import {
  GoogleAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  User as FirebaseUser
} from 'firebase/auth';

// Echte Google login.
// 1. Voorkeur: Google Identity Services (GIS) knop -> ID token -> Firebase signInWithCredential.
//    Werkt ook in een geïnstalleerde PWA (geen popup of redirect nodig).
// 2. Geen GIS client ID geconfigureerd: Firebase signInWithPopup, met redirect als popup geblokkeerd wordt.

const env = (import.meta as any).env || {};
const GOOGLE_CLIENT_ID: string = env.VITE_GOOGLE_CLIENT_ID || '';

const toAppUser = (fbUser: FirebaseUser): User => ({
  id: fbUser.uid,
  name: fbUser.displayName || fbUser.email?.split('@')[0] || 'Gebruiker',
  email: (fbUser.email || '').toLowerCase(),
  photoUrl: fbUser.photoURL || undefined
});

const decodeJwtPayload = (jwt: string): any => {
  const base64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
  );
  return JSON.parse(json);
};

const describeAuthError = (err: any): string => {
  const code: string = err?.code || '';
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return 'Inloggen geannuleerd.';
  if (code === 'auth/unauthorized-domain') return 'Dit domein staat niet in Firebase Auth → Instellingen → Geautoriseerde domeinen.';
  if (code === 'auth/operation-not-allowed') return 'Google login staat niet aan in Firebase Auth → Sign-in method.';
  if (code === 'auth/configuration-not-found') return 'Firebase Authentication is nog niet ingeschakeld (Console → Authentication → Aan de slag → Google).';
  if (code === 'auth/api-key-not-valid.-please-pass-a-valid-api-key.' || code.startsWith('auth/api-key-not-valid')) return 'De Firebase API key is ongeldig. Controleer VITE_FIREBASE_API_KEY.';
  if (code === 'auth/invalid-credential') return 'Google token geweigerd door Firebase. Staat de OAuth client ID op de whitelist bij de Google provider?';
  if (code === 'auth/network-request-failed') return 'Geen internetverbinding.';
  return `Inloggen mislukt${code ? ` (${code})` : ''}.`;
};

// GIS wordt één keer geïnitialiseerd; de callback gaat naar de knop die als laatste gemount is.
let gisInitialized = false;
let activeCredentialHandler: ((credential: string) => void) | null = null;

const waitForGis = (timeoutMs = 6000): Promise<any> =>
  new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      const gis = (window as any).google?.accounts?.id;
      if (gis) return resolve(gis);
      if (Date.now() - start > timeoutMs) return reject(new Error('Google script niet geladen'));
      setTimeout(check, 100);
    };
    check();
  });

export const isGoogleLoginConfigured = () => !!GOOGLE_CLIENT_ID || (!!auth && isFirebaseReady());

export const GoogleLoginButton: React.FC<{
  onLogin: (user: User) => void;
  className?: string;
  label?: string;
}> = ({ onLogin, className = '', label = 'Doorgaan met Google' }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [gisReady, setGisReady] = useState(false);
  const gisButtonRef = useRef<HTMLDivElement>(null);
  const onLoginRef = useRef(onLogin);
  onLoginRef.current = onLogin;

  const handleCredential = async (credential: string) => {
    setErrorMsg(null);
    setIsLoading(true);
    try {
      if (auth && isFirebaseReady()) {
        const result = await signInWithCredential(auth, GoogleAuthProvider.credential(credential));
        onLoginRef.current(toAppUser(result.user));
      } else {
        // Geen Firebase: wel een echt Google account, maar alleen lokale opslag.
        const p = decodeJwtPayload(credential);
        onLoginRef.current({
          id: `google-${p.sub}`,
          name: p.name || p.email,
          email: (p.email || '').toLowerCase(),
          photoUrl: p.picture
        });
      }
    } catch (err: any) {
      console.error('Google login (GIS) failed', err);
      setErrorMsg(describeAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Render de officiële Google knop als er een client ID is
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    activeCredentialHandler = handleCredential;

    waitForGis()
      .then(gis => {
        if (cancelled || !gisButtonRef.current) return;
        if (!gisInitialized) {
          gis.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (resp: { credential?: string }) => {
              if (resp.credential && activeCredentialHandler) activeCredentialHandler(resp.credential);
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            use_fedcm_for_prompt: true,
            use_fedcm_for_button: true
          });
          gisInitialized = true;
        }
        gis.renderButton(gisButtonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
          logo_alignment: 'center',
          locale: 'nl',
          width: Math.min(400, gisButtonRef.current.offsetWidth || 320)
        });
        setGisReady(true);
      })
      .catch(err => {
        console.warn('GIS niet beschikbaar, val terug op Firebase popup', err);
      });

    return () => {
      cancelled = true;
      if (activeCredentialHandler === handleCredential) activeCredentialHandler = null;
    };
  }, []);

  const handleFirebaseSignIn = async () => {
    setErrorMsg(null);
    if (!auth || !isFirebaseReady()) {
      setErrorMsg('Google login is nog niet geconfigureerd (Firebase of VITE_GOOGLE_CLIENT_ID ontbreekt).');
      return;
    }
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    try {
      const result = await signInWithPopup(auth, provider);
      onLogin(toAppUser(result.user));
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        // Redirect: na terugkomst pakt onAuthStateChanged in App.tsx de gebruiker op
        try {
          await signInWithRedirect(auth, provider);
        } catch (redirectErr: any) {
          console.error('Google login (redirect) failed', redirectErr);
          setErrorMsg(describeAuthError(redirectErr));
        }
        return;
      }
      console.error('Google login (popup) failed', err);
      setErrorMsg(describeAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const showGisButton = !!GOOGLE_CLIENT_ID && gisReady;

  return (
    <div className="w-full">
      {/* Officiële Google knop (GIS) */}
      {GOOGLE_CLIENT_ID && (
        <div
          ref={gisButtonRef}
          className={`w-full flex justify-center ${showGisButton ? '' : 'hidden'} ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        />
      )}

      {/* Firebase popup knop (ook als laadweergave zolang GIS nog niet klaar is) */}
      {!showGisButton && (
        <button
          onClick={handleFirebaseSignIn}
          disabled={isLoading}
          className={`w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-2xl bg-white dark:bg-white/10 text-gray-800 dark:text-white font-bold text-sm border border-gray-200 dark:border-white/15 shadow-sm hover:shadow-md active:scale-[0.98] transition-all cursor-pointer disabled:opacity-60 ${className}`}
        >
          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
          </svg>
          <span>{isLoading ? 'Verbinden...' : label}</span>
        </button>
      )}

      {isLoading && showGisButton && (
        <p className="text-[11px] text-gray-500 text-center mt-2">Inloggen...</p>
      )}

      {errorMsg && (
        <div className="mt-2 p-2.5 bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-[11px] rounded-xl flex items-start gap-2 text-left">
          <span className="material-symbols-outlined text-[16px]">error</span>
          <span>{errorMsg}</span>
        </div>
      )}
    </div>
  );
};
